import type { Product } from "@/components/product-step/types";
import { FOLLOWUP_BLOOD_TEST_STRIPE_ID } from "@/utils/bloodTestVisibility";

/**
 * `?from=<source>` — who sent the user here.
 *
 * One param carrying the entry context, so a linking app doesn't have to
 * hand-assemble the low-level params (and doesn't have to know our Supabase
 * product UUIDs, which differ per environment). The flow resolves the source to
 * the same starting state a `?products=<uuid>` deep link would produce.
 *
 * Deliberately a plain source → defaults map: a second source is one entry
 * here, not a refactor. Everything it yields is a DEFAULT — an explicit
 * `?products=` / `?packageType=` in the URL still wins (enforced by the caller
 * in `initializeFromUrl`).
 */
export interface EntrySourceDefaults {
  /**
   * Stripe product id of the package to preselect. Stripe ids are stable across
   * environments; the Supabase UUID is not, so it is resolved at runtime.
   */
  packageStripeProductId: string;
}

const ENTRY_SOURCES: Record<string, EntrySourceDefaults> = {
  /** "Book your follow-up blood test" CTA on the health report. */
  reportBloodFollowupCTA: {
    packageStripeProductId: FOLLOWUP_BLOOD_TEST_STRIPE_ID,
  },
};

/** Defaults for an entry source. Unknown or absent source → null (no-op). */
export const entrySourceDefaults = (
  source: string | null | undefined,
): EntrySourceDefaults | null => (source && ENTRY_SOURCES[source]) || null;

/**
 * Supabase product id the `?from=` source wants preselected, resolved against
 * the loaded catalog. Null when the source is unknown/absent or the package
 * isn't in this market's catalog.
 */
export const entrySourcePackageId = (
  source: string | null | undefined,
  products: Product[] | undefined,
): string | null => {
  const defaults = entrySourceDefaults(source);
  if (!defaults) return null;
  return (
    products?.find(
      (p) =>
        p.product_type === "package" &&
        p.stripe_product_id === defaults.packageStripeProductId,
    )?.id ?? null
  );
};

/**
 * The `?products=` value a `?from=` link should be rewritten to, or null to
 * leave the URL alone.
 *
 * Pure, and it owns the precedence rule, so the store does not have to: an
 * explicit `?products=` or `?packageType=` in the link always wins, because
 * `from=` only ever supplies DEFAULTS.
 *
 * Rewriting the URL rather than injecting the id further down
 * `initializeFromUrl` is the whole point. `from=` claims to be an alias onto
 * `?products=`, and this is what makes that true: once the param is there, every
 * existing reader sees it, including the ones that run BEFORE the old injection
 * point and read `window.location` directly (the loadSession-in-progress stash,
 * the session-conflict check, and loadSession itself). Resolved later, those
 * readers saw an empty URL and the deep link was silently dropped for anyone
 * with a restored session — which is every returning customer, and a follow-up
 * CTA has no other audience.
 */
export const entrySourceUrlProducts = (
  search: string,
  products: Product[] | undefined,
): string | null => {
  const params = new URLSearchParams(search);
  if (params.get("packageType") || params.get("products")) return null;
  return entrySourcePackageId(params.get("from"), products);
};
